import { yupResolver } from '@hookform/resolvers/yup';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { match } from 'react-router-dom';
import { toast } from 'sonner';
import { useStripePublishableKeyByToken } from '../../../../../../../queries';
import { useClientStripeSessionByToken } from '../../../../../../../queries/Surveys/useClientStripeSessionByToken';
import { Button } from '../../../../../../../web/components-v2/Button';
import { Pill } from '../../../../../../../web/components-v2/Pill/Pill';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../../../../web/components/ConfirmationDialog';
import { LoadingLogo } from '../../../../../../../web/components/LoadingLogo';
import { Modal } from '../../../../../../../web/components/Modal';
import { WarningIcon } from '../../../../../../../web/components/svgs/icons/WarningIcon';
import { CheckoutFormProviderWrap, stripeCheckoutStyleSheet } from '../../../../../../components/StripeCheckout';
import {
	IEventRegistrationSurvey,
	IEventSurveyResponse,
	ILineItem,
	IOperationResultNoValue,
	IRecipient,
} from '../../../../../../models/Api';
import { AttendanceRadioButtons } from './components/AttendanceRadioButtons';
import { AttendeeInfoFields } from './components/AttendeeInfoFields';
import { CustomFormFields } from './components/CustomFormFields';
import { GuestFields } from './components/GuestFields';
import { PaymentSummary } from './components/PaymentSummary';
import { TicketHolderFields } from './components/TicketHolderFields';
import { TicketSelector } from './components/TicketSelector';
import {
	useEventSurveyFormInitialization,
	useEventSurveyFormSubmission,
	useEventSurveyMutations,
	useEventSurveyValidation,
	usePaymentProcessing,
	useTicketHolderManagement,
} from './hooks';
import { styleSheet } from './styles';
import { getRsvpBackground } from './utils';

type Props = {
	survey: IEventRegistrationSurvey;
	onResponseSubmitted?(response: IEventSurveyResponse): void;
	surveyResponse: IEventSurveyResponse;
	routeMatch?: match<{ token?: string }>; // Make it optional for backward compatibility
	loading: boolean;
	isEdit: boolean;
	setLoading: (loading: boolean) => void;
	cancelEdit: () => void;
	onPaymentStarted?: () => void;
	onPaymentCompleted?: (sessionId: string) => void;
	isAttending: boolean | null;
};

const NotAttendingEventCancelOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Continue',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];
const PaidAttendOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isCta: true,
		representedObject: false,
		title: 'Close',
	},
];

interface EventCheckoutContentProps {
	surveyResponse: IEventSurveyResponse;
	stripePublicId: string;
	token: string;
	ticketsOrder: number;
	lineItems?: ILineItem[];
	pooledResponseBody?: IEventSurveyResponse;
	onSuccessClose: (sessionId: string) => void;
	onCloseModal: (sessionId: string) => void;
	onError: (error: IOperationResultNoValue) => void;
}

export const EventCheckoutContent = ({
	surveyResponse,
	stripePublicId,
	token,
	ticketsOrder,
	lineItems,
	pooledResponseBody,
	onSuccessClose,
	onCloseModal,
	onError,
}: EventCheckoutContentProps) => {
	const stripeSession = useClientStripeSessionByToken({ onError });
	const clientSecretPromise = React.useMemo(() => {
		return stripeSession.mutateAsync({
			token,
			quantity: ticketsOrder,
			response: pooledResponseBody ?? surveyResponse,
			items: lineItems,
			_type: 'EventRegistrationCheckout',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const defaultValues = React.useMemo(
		() => ({
			billingAddress: {
				name: surveyResponse?.attendee
					? `${surveyResponse.attendee.firstName} ${surveyResponse.attendee.lastName}`
					: undefined,
				address: { country: 'US' as const },
			},
		}),
		[surveyResponse]
	);

	const orderSummary = (checkout: { lineItems?: { id: string; name: string; quantity: number }[] }) => (
		<>
			{checkout.lineItems?.map(item => (
				<div key={item.id} className={css(stripeCheckoutStyleSheet.orderInfo)}>
					<p>
						{item.name} <span className={css(stripeCheckoutStyleSheet.timesSymbol)}>&#215;</span>{' '}
						<strong>{item.quantity}</strong>
					</p>
				</div>
			))}
		</>
	);

	const submitButtonLabel = (checkout: { total?: { total?: { amount?: string } } }) =>
		`Pay ${checkout.total?.total?.amount ?? ''}`;

	return (
		<CheckoutFormProviderWrap
			stripePublicId={stripePublicId}
			clientSecretPromise={clientSecretPromise}
			defaultValues={defaultValues}
			prefilledEmail={surveyResponse?.attendee?.email ?? ''}
			onSuccessClose={onSuccessClose}
			onCloseModal={onCloseModal}
			sectionTitle='Your Order'
			orderSummary={orderSummary}
			submitButtonLabel={submitButtonLabel}
			loggingName='EventRegistrationCheckoutForm'
		/>
	);
};

interface ITicketHolder {
	id?: string; // Unique ID from backend or generated UUID
	ticketId: string;
	ticketName: string; // For display (e.g., "Basic Ticket")
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	isPaid?: boolean; // Whether this ticket has been paid for
}

interface IFormValues {
	isAttending: boolean | null;
	isPaid: boolean;
	bringingGuest?: boolean;
	email?: string;
	firstName?: string;
	lastName?: string;
	phone?: string;
	guests: IRecipient[];
	customProperties: Record<string, string>;
	ticketSelections?: Record<string, number>; // ticketId → quantity
	ticketHolders?: ITicketHolder[]; // One entry per ticket purchased
}

const DEFAULT_VALUES: IFormValues = {
	isAttending: null,
	isPaid: false,
	bringingGuest: false,
	email: '',
	guests: [{ firstName: '', lastName: '', isPaid: false }],
	firstName: '',
	lastName: '',
	phone: '',
	customProperties: {},
	ticketSelections: {},
	ticketHolders: [],
};
export const ErrorMessage = ({ message }: { message: string }) => (
	<p className={`${css(styleSheet.errorMessage)} ${css(styleSheet.noMargin)}`}>{message}</p>
);

export const EventSurveyForm = ({
	survey,
	onResponseSubmitted,
	surveyResponse,
	routeMatch,
	loading,
	isEdit,
	setLoading,
	cancelEdit,
	onPaymentStarted,
	onPaymentCompleted,
	isAttending,
}: Props) => {
	// Use the passed routeMatch if available, or create an empty one
	const { params: urlParams = {} } = routeMatch || { params: {} };

	const processingPayment = React.useRef<boolean>(false);
	const pendingAttendanceSelection = React.useRef<boolean | null>(null);

	const { data: stripePublicId } = useStripePublishableKeyByToken({
		accessToken: urlParams.token,
		refetchOnWindowFocus: false,
	});

	const [showCheckoutForm, setShowCheckoutForm] = React.useState<boolean>(false);
	// Removed isPollingPayment state - no longer polling after payment
	const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

	/**
	 * BUG FIX: Track the latest survey response from backend
	 *
	 * When the form is submitted and the backend returns a response, we need to pass that
	 * latest response to the checkout modal (not stale prop data). This state is updated
	 * in handleResponseSubmitted callback.
	 */
	const [latestSurveyResponse, setLatestSurveyResponse] = React.useState<IEventSurveyResponse | undefined>(
		surveyResponse
	);

	const [showCancelPaidConfirmationModal, setShowCancelPaidConfirmationModal] = React.useState<boolean>(false);
	const [showPaidAttendConfirmationModal, setShowPaidAttendConfirmationModal] = React.useState<boolean>(false);
	const [ticketsOrder, setTicketsOrder] = React.useState<number>(0);
	const [paidTickets, setPaidTickets] = React.useState<number>(0);

	/**
	 * Track which ticket holder is the primary attendee using stable ID
	 *
	 * This ID is managed by useTicketHolderManagement and useEventSurveyFormInitialization:
	 * - When loading from backend: uses recipient.id from attendee
	 * - When creating new holders: generates UUID with crypto.randomUUID()
	 * - When submitting: maps the holder with this ID to attendee
	 *
	 * Using IDs instead of array indices prevents bugs when:
	 * - Ticket holders array is reordered
	 * - Tickets are added/removed
	 * - User changes primary selection
	 */
	const [primaryTicketHolderId, setPrimaryTicketHolderId] = React.useState<string | null>(null);

	const validationSchema = useEventSurveyValidation({ survey });

	const methods = useForm({
		defaultValues: DEFAULT_VALUES,
		resolver: yupResolver(validationSchema, { abortEarly: false }),
	});
	const { handleSubmit, control, formState, getValues, watch, setValue, reset } = methods;
	const formValues = watch();

	const { handlePrimaryTicketHolderChange, handleTicketSelectionChange } = useTicketHolderManagement({
		survey,
		surveyResponse,
		formValues,
		primaryTicketHolderId,
		setPrimaryTicketHolderId,
		getValues,
		setValue,
	});

	const handleAttendingYes = React.useCallback(() => {
		if (
			survey?.isPaidEvent &&
			surveyResponse?.payments?.some(p => p.status === 'PaymentSucceeded') &&
			!surveyResponse?.isAttending
		) {
			setShowPaidAttendConfirmationModal(true);

			return;
		}
		setValue('isAttending', true);
	}, [setValue, survey?.isPaidEvent, surveyResponse?.isAttending, surveyResponse?.payments]);

	const handleAttendingNo = React.useCallback(() => {
		if (surveyResponse?.payments?.some(p => p.status === 'PaymentSucceeded')) {
			setShowCancelPaidConfirmationModal(true);
			return;
		}
		setValue('isAttending', false);
		setValue('guests', DEFAULT_VALUES.guests);
	}, [setValue, surveyResponse?.payments]);

	const {
		deleteStripeSession,
		resetUrlToToken,
		getPaidTicketQuantities,
		getTotalTicketsOrder,
		formattedAmount,
		buildLineItems,
	} = usePaymentProcessing({
		survey,
		surveyResponse,
		formValues,
		processingPayment,
		setShowCheckoutForm,
		ticketsOrder,
		setTicketsOrder,
		setPaidTickets,
		urlParams,
	});

	// Removed usePaymentPolling - no longer polling, using checkout session endpoint instead

	const handleOpenCheckoutForm = React.useCallback(() => {
		// Recalculate ticketsOrder before opening the form
		const currentTicketsOrder = getTotalTicketsOrder();
		setTicketsOrder(currentTicketsOrder);

		setShowCheckoutForm(true);
		onPaymentStarted?.(); // Notify parent component
		processingPayment.current = true;
		reset({ ...formValues });
	}, [getTotalTicketsOrder, formValues, reset, onPaymentStarted]);

	// Determine if checkout should open after mutation success
	const shouldOpenCheckout = React.useCallback(() => {
		return Boolean(formValues.isAttending && ticketsOrder > 0 && survey?.isPaidEvent);
	}, [formValues.isAttending, ticketsOrder, survey?.isPaidEvent]);

	/**
	 * Wrap onResponseSubmitted to also update local state
	 *
	 * This ensures that when the backend returns the survey response after submission,
	 * we capture it in latestSurveyResponse so the checkout modal receives the most
	 * recent data (not stale prop data).
	 */
	const handleResponseSubmitted = React.useCallback(
		(response: IEventSurveyResponse) => {
			setLatestSurveyResponse(response);
			if (onResponseSubmitted) {
				onResponseSubmitted(response);
			}
		},
		[onResponseSubmitted]
	);

	// Extract mutations to dedicated hook
	const { createResponseMutation, updateResponseMutation } = useEventSurveyMutations({
		formValues,
		onResponseSubmitted: handleResponseSubmitted,
		setIsSubmitting,
	});

	/**
	 * Handle modal opening timing and loading state - wait for state to update after submission
	 *
	 * This useEffect ensures the payment modal opens after submission completes and:
	 * 1. Backend response is received (latestSurveyResponse updated)
	 * 2. React state updates complete
	 * 3. Form re-initialization runs with fresh data (loading = false)
	 * 4. ONLY THEN open modal with fresh, complete data
	 *
	 * It also ensures loading indicator is cleared after successful submission,
	 * regardless of whether checkout modal opens or not.
	 *
	 * This prevents race conditions where modal opens with stale data.
	 */
	React.useEffect(() => {
		// Only process if we're currently submitting (indicates response is from a submission)
		// AND form initialization is complete (!loading)
		if (isSubmitting && latestSurveyResponse && !loading) {
			// If processing payment and should open checkout, open modal first
			if (processingPayment.current && !showCheckoutForm && shouldOpenCheckout()) {
				handleOpenCheckoutForm();
				// Keep isSubmitting=true during payment flow to prevent prop sync from overwriting
			} else {
				// No payment needed, clear loading immediately
				setIsSubmitting(false);
			}
		}
	}, [latestSurveyResponse, showCheckoutForm, shouldOpenCheckout, handleOpenCheckoutForm, isSubmitting, loading]);

	// Extract form submission logic to dedicated hook
	const { handleSave, responseBody } = useEventSurveyFormSubmission({
		survey,
		surveyResponse,
		primaryTicketHolderId,
		processingPayment,
		pendingAttendanceSelection,
		getValues,
		urlParams,
		createResponseMutation,
		updateResponseMutation,
		setIsSubmitting,
	});

	const stats = survey?.stats?.responseStats?.find(x => x.category === 'Total');

	const isEventFull =
		stats && survey?.attendeeOptions?.maximumCapacity
			? stats.attending >= survey.attendeeOptions.maximumCapacity
			: false;

	const isPastEvent = React.useMemo(() => {
		const dueDateString = survey?.attendeeOptions?.registrationDeadline || survey?.eventInformation?.startTime;
		// Allow RSVPs through the end of the deadline day (23:59:59)
		return dueDateString ? moment(dueDateString).endOf('day').isBefore(new Date()) : false;
	}, [survey]);

	const surveyWarningText = React.useMemo(() => {
		let text = '';
		if (isPastEvent) {
			text = 'Sorry, the event registration deadline has passed.';
		}
		if (isEventFull) {
			text = 'Sorry, the event has reached the maximum capacity of attendees.';
		}
		return `${text} Please reach out to the event organizer for details.`;
	}, [isEventFull, isPastEvent]);

	const rsvpBackground = React.useMemo(
		() => getRsvpBackground(formValues?.isAttending, isPastEvent, isEventFull),
		[formValues?.isAttending, isPastEvent, isEventFull]
	);

	// Sync latestSurveyResponse with prop when it changes (e.g., after refetch)
	// Skip sync during form submission and initialization to avoid overwriting fresh mutation responses
	React.useEffect(() => {
		if (surveyResponse && !isSubmitting && !loading) {
			setLatestSurveyResponse(surveyResponse);
		}
	}, [surveyResponse, isSubmitting, loading]);

	/**
	 * Build fresh survey response from current form values
	 * This ensures the checkout modal always receives the latest data from the form
	 */
	const buildSurveyResponseFromForm = React.useCallback((): IEventSurveyResponse => {
		const values = getValues();
		const baseResponse = latestSurveyResponse || surveyResponse || {};

		const response: IEventSurveyResponse = {
			...baseResponse,
			anonymousGuestCount: 0,
			attendee: {
				email: values.email?.trim() || '',
				firstName: values.firstName?.trim() || '',
				lastName: values.lastName?.trim() || '',
				phone: values.phone?.trim(),
				isPaid: surveyResponse?.attendee?.isPaid || false,
			},
			guests: [],
			isAttending: values.isAttending,
		};

		// Handle ticket holders for paid events with tiered ticketing
		if (survey?.isPaidEvent && values.ticketHolders && values.ticketHolders.length > 0) {
			const primaryHolder = values.ticketHolders.find(h => h.id === primaryTicketHolderId);
			if (primaryHolder) {
				const primaryTicket = survey.attendeeOptions?.eventTickets?.find(t => t.id === primaryHolder.ticketId);
				response.attendee = {
					id: primaryHolder.id,
					email: primaryHolder.email?.trim() || '',
					firstName: primaryHolder.firstName?.trim() || '',
					lastName: primaryHolder.lastName?.trim() || '',
					phone: primaryHolder.phone?.trim(),
					isPaid: primaryHolder.isPaid || surveyResponse?.attendee?.isPaid || false,
					ticket: primaryTicket,
				};
			}

			response.guests = values.ticketHolders
				.filter(holder => holder.id !== primaryTicketHolderId)
				.map((holder, index) => {
					const ticket = survey.attendeeOptions?.eventTickets?.find(t => t.id === holder.ticketId);
					return {
						id: holder.id,
						firstName: holder.firstName?.trim() || 'Guest',
						lastName: holder.lastName?.trim() || `Attendee ${index + 1}`,
						email: holder.email?.trim(),
						phone: holder.phone?.trim(),
						isPaid: holder.isPaid || false,
						ticket,
					};
				});
		} else if (values.bringingGuest) {
			// Legacy guest handling
			const guests = values.guests.filter(guest => guest.firstName?.trim() && guest.lastName?.trim());
			const processedGuests = guests.map((guest, index) => {
				const existingGuest = surveyResponse?.guests?.[index];
				return {
					...guest,
					isPaid: existingGuest?.isPaid || false,
				};
			});
			response.guests = processedGuests;
		}

		if (values.customProperties && Object.values(values.customProperties).length) {
			response.customProperties = values.customProperties;
		}

		return response;
	}, [getValues, latestSurveyResponse, surveyResponse, survey, primaryTicketHolderId]);

	// Initialize form with existing survey response data
	// Use latestSurveyResponse (from mutation success) if available, otherwise fall back to prop
	useEventSurveyFormInitialization({
		survey,
		surveyResponse: latestSurveyResponse || surveyResponse,
		urlParams,
		isAttending,
		processingPayment,
		pendingAttendanceSelection,
		setValue,
		getValues,
		setLoading,
		DEFAULT_VALUES,
		setPrimaryTicketHolderId,
	});

	const handleCloseCheckoutForm = React.useCallback(
		(id: string, cancel: boolean, error?: IOperationResultNoValue) => {
			setShowCheckoutForm(false);
			setIsSubmitting(false); // Clear loading state when checkout is closed
			if (id && !cancel) {
				processingPayment.current = false;
				pendingAttendanceSelection.current = null; // Clear pending selection on cancel
				deleteStripeSession.mutate({
					sessionId: id,
					token: urlParams.token,
				});
			} else if (error) {
				toast.error(error?.systemMessage);
			}
		},
		[deleteStripeSession, urlParams.token]
	);

	const onSuccessCloseCheckoutForm = React.useCallback(
		(sessionId: string) => {
			if (window) {
				window.onbeforeunload = null;
			}
			setShowCheckoutForm(false);
			setIsSubmitting(false); // Clear loading state after successful payment
			processingPayment.current = false;
			pendingAttendanceSelection.current = null; // Clear pending selection after payment
			resetUrlToToken();
			cancelEdit();

			// Notify parent that payment has been completed with sessionId
			// NO POLLING - parent will query checkout session endpoint with sessionId
			onPaymentCompleted?.(sessionId);

			// NO POLLING - checkout session endpoint will return the complete response
		},
		[resetUrlToToken, cancelEdit, onPaymentCompleted]
	);

	React.useEffect(() => {
		const currentTicketsOrder = getTotalTicketsOrder();
		// Only open checkout modal if form is valid (no errors) and all conditions are met
		const hasNoErrors = Object.keys(formState.errors).length === 0;

		if (formState.isSubmitted && hasNoErrors && processingPayment.current && currentTicketsOrder > 0) {
			setTicketsOrder(currentTicketsOrder);
			setShowCheckoutForm(true);
		}
	}, [
		formValues.isAttending,
		survey?.isPaidEvent,
		surveyResponse?.id,
		getTotalTicketsOrder,
		formState.isSubmitted,
		formState.errors,
	]);

	return (
		<FormProvider {...methods}>
			{loading ? (
				<LoadingLogo size={100} />
			) : (
				<form className={css(styleSheet.rsvpForm)} onSubmit={handleSubmit(handleSave)}>
					{/* Removed polling indicator - no longer needed */}

					{!isPastEvent && !isEventFull && (
						<AttendeeInfoFields
							requirePhoneNumber={survey?.attendeeOptions?.requirePhoneNumber}
							isPaid={surveyResponse?.attendee?.isPaid ?? false}
						/>
					)}
					<AttendanceRadioButtons
						rsvpBackground={rsvpBackground}
						isPastEvent={isPastEvent}
						isEventFull={isEventFull}
						surveyWarningText={surveyWarningText}
						onAttendingYes={handleAttendingYes}
						onAttendingNo={handleAttendingNo}
					/>

					{/* Ticket Selector - only for paid events with tiered tickets */}
					{formValues.isAttending &&
						survey?.isPaidEvent &&
						survey?.attendeeOptions?.eventTickets &&
						survey?.attendeeOptions?.eventTickets?.length > 0 && (
							<>
								<TicketSelector
									tickets={survey.attendeeOptions.eventTickets}
									selections={formValues.ticketSelections || {}}
									onSelectionChange={handleTicketSelectionChange}
									minQuantities={getPaidTicketQuantities()}
								/>
								{formState.errors.ticketSelections && (
									<p className={css(styleSheet.errorMessage)}>{String(formState.errors.ticketSelections.message)}</p>
								)}
								<TicketHolderFields
									tickets={survey.attendeeOptions.eventTickets}
									ticketSelections={formValues.ticketSelections || {}}
									ticketHolders={formValues.ticketHolders || []}
									control={control}
									errors={formState.errors}
									disabled={false}
									primaryTicketHolderId={primaryTicketHolderId}
									onPrimaryTicketHolderChange={handlePrimaryTicketHolderChange}
									requireGuestNames={survey.attendeeOptions.requireGuestNames}
									requirePhoneNumber={survey?.attendeeOptions?.requirePhoneNumber}
								/>
							</>
						)}

					{!isPastEvent && !isEventFull && survey?.attendeeOptions?.guestLimit && formValues.isAttending ? (
						<GuestFields
							guestLimit={survey.attendeeOptions.guestLimit}
							requireGuestNames={survey.attendeeOptions.requireGuestNames}
							isPaidEvent={survey.isPaidEvent}
							hasPaidGuests={Boolean(surveyResponse?.guests?.length || surveyResponse?.anonymousGuestCount)}
							guestCount={surveyResponse?.guests?.length || surveyResponse?.anonymousGuestCount}
							DEFAULT_VALUES={DEFAULT_VALUES}
						/>
					) : null}
					{!survey?.customFormIsDisabled && formValues.isAttending && survey?.customForm?.fields ? (
						<CustomFormFields fields={survey.customForm.fields} />
					) : null}
					<PaymentSummary
						isPaidEvent={survey?.isPaidEvent}
						ticketSelections={formValues.ticketSelections}
						tickets={survey?.attendeeOptions?.eventTickets}
						ticketsOrder={ticketsOrder}
						paidTickets={paidTickets}
						formattedAmount={formattedAmount}
						getPaidTicketQuantities={getPaidTicketQuantities}
						isLegacyEvent={Boolean(
							survey?.attendeeOptions?.costPerAttendee && !survey?.attendeeOptions?.eventTickets?.length
						)}
						costPerAttendee={survey?.attendeeOptions?.costPerAttendee}
					/>
					{formValues.isAttending && !ticketsOrder && survey?.isPaidEvent && paidTickets > 0 ? (
						<div className={css(styleSheet.paidTickets)}>
							<Pill label={`Paid Tickets: ${paidTickets}`} variant='label' color={'green'} />
						</div>
					) : null}

					<footer className={css(styleSheet.actions)}>
						{!isPastEvent && !isEventFull ? (
							<>
								<div className={css(styleSheet.divider)} />
								<div className={css(styleSheet.buttonContainer)}>
									<Button
										type='submit'
										variant='primary'
										styles={[styleSheet.formSubmitButton]}
										disabled={isSubmitting}
										isLoading={isSubmitting}
									>
										{isSubmitting
											? 'Processing...'
											: surveyResponse?.id
												? ticketsOrder > 0 && survey?.isPaidEvent
													? 'Pay for Event'
													: 'Update Response'
												: formValues.isAttending && survey?.isPaidEvent
													? 'Proceed to Payment'
													: 'RSVP'}
									</Button>

									{surveyResponse?.id && isEdit ? (
										<Button type='button' variant='secondary' onClick={cancelEdit}>
											Cancel
										</Button>
									) : null}
								</div>
							</>
						) : null}
					</footer>
					<ConfirmationDialog
						key='not-attending-event-cancel-confirmation'
						icon={<WarningIcon />}
						modalProps={{
							isOpen: showCancelPaidConfirmationModal,
							onRequestClose: (_, cancel) => {
								if (cancel) {
									setShowCancelPaidConfirmationModal(false);
									return;
								}
								setValue('isAttending', false);
								setShowCancelPaidConfirmationModal(false);
								handleSave();
							},
						}}
						options={NotAttendingEventCancelOptions}
						title='Refund Warning'
					>
						<p className={css(styleSheet.warningMessage)}>
							You are changing your attendance to <strong>Not Attending</strong>.{' '}
							<span>This will not initiate a refund</span>. Please reach out to the event host for their refund policy.
						</p>
					</ConfirmationDialog>
					<ConfirmationDialog
						key='wants-to-attend-event-confirmation'
						icon={<WarningIcon />}
						modalProps={{
							isOpen: showPaidAttendConfirmationModal,
							onRequestClose: () => {
								setShowPaidAttendConfirmationModal(false);
							},
						}}
						options={PaidAttendOptions}
						title='Attendance Warning'
					>
						<p className={css(styleSheet.warningMessage)}>
							You have already responded as not attending. Please reach out to the event host to change your response.
						</p>
					</ConfirmationDialog>
					<Modal
						isOpen={showCheckoutForm}
						onRequestClose={handleCloseCheckoutForm}
						useDefaultHeader={false}
						shouldCloseOnOverlayClick={false}
					>
						{stripePublicId ? (
							<EventCheckoutContent
								surveyResponse={buildSurveyResponseFromForm()}
								stripePublicId={stripePublicId}
								token={urlParams.token ?? ''}
								ticketsOrder={ticketsOrder}
								lineItems={buildLineItems()}
								pooledResponseBody={responseBody}
								onSuccessClose={onSuccessCloseCheckoutForm}
								onCloseModal={sessionId => handleCloseCheckoutForm(sessionId, false)}
								onError={error => handleCloseCheckoutForm('', false, error)}
							/>
						) : null}
					</Modal>

					{/* Loading overlay during form submission */}
					{isSubmitting && (
						<div className={css(styleSheet.submitOverlay)}>
							<LoadingLogo size={60} />
							<p className={css(styleSheet.submitOverlayText)}>Processing your registration...</p>
						</div>
					)}
				</form>
			)}
		</FormProvider>
	);
};
